import React from 'react';

import { UploadedReceipt } from '../../../types';

import ReceiptStatusBadge from './ReceiptStatusBadge';

interface ImportStatusSummaryProps {
  receipts: UploadedReceipt[];
}

const ImportStatusSummary: React.FC<ImportStatusSummaryProps> = ({ receipts }) => {
  const totalReceipts = receipts.length;

  // Calculate status counts
  const statusCounts = receipts.reduce(
    (acc, receipt) => {
      acc[receipt.status] = (acc[receipt.status] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  const allSameStatus = receipts.length > 0 &&
    receipts.every(receipt => receipt.status === receipts[0].status);
  const commonStatus = allSameStatus ? receipts[0].status : null;

  return (
    <div className="flex items-center space-x-8">
      {/* Receipt Count */}
      <div>
        <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">
          {totalReceipts}
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {totalReceipts === 1 ? 'Receipt' : 'Receipts'}
        </p>
      </div>

      {/* Status Breakdown */}
      <div className="flex flex-wrap gap-4">
        {allSameStatus && commonStatus ? (
          // Show "All <status>" when all receipts have the same status
          <ReceiptStatusBadge status={commonStatus} showAll />
        ) : (
          // Show individual counts when receipts have different statuses
          <>
            {statusCounts.pending > 0 && (
              <ReceiptStatusBadge status="pending" count={statusCounts.pending} />
            )}
            {statusCounts['text-extracting'] > 0 && (
              <ReceiptStatusBadge status="text-extracting" count={statusCounts['text-extracting']} />
            )}
            {statusCounts.ready > 0 && (
              <ReceiptStatusBadge status="ready" count={statusCounts.ready} />
            )}
            {statusCounts.generated > 0 && (
              <ReceiptStatusBadge status="generated" count={statusCounts.generated} />
            )}
            {statusCounts.modified > 0 && (
              <ReceiptStatusBadge status="modified" count={statusCounts.modified} />
            )}
            {statusCounts.imported > 0 && (
              <ReceiptStatusBadge status="imported" count={statusCounts.imported} />
            )}
            {statusCounts['text-extraction-failed'] > 0 && (
              <ReceiptStatusBadge status="text-extraction-failed" count={statusCounts['text-extraction-failed']} />
            )}
            {statusCounts.generating > 0 && (
              <ReceiptStatusBadge status="generating" count={statusCounts.generating} />
            )}
            {statusCounts['generation-failed'] > 0 && (
              <ReceiptStatusBadge status="generation-failed" count={statusCounts['generation-failed']} />
            )}
            {statusCounts.importing > 0 && (
              <ReceiptStatusBadge status="importing" count={statusCounts.importing} />
            )}
            {statusCounts['import-failed'] > 0 && (
              <ReceiptStatusBadge status="import-failed" count={statusCounts['import-failed']} />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ImportStatusSummary;
